import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import AddOrEditTagDrawer from "./AddOrEditTagDrawer";
import {
  deleteTrackerTagApi,
  getTrackerTagsApi,
} from "../../../DAL/trackerTag/trackerTag";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary)";

const tagId = (tag) => String(tag?._id || tag?.id || "");

/**
 * Tag library add / edit / delete without leaving the page (tracker builder).
 * onChanged({ type: "saved" | "deleted", tag, prevName }) after each change.
 */
export default function ManageTagsDrawer({ open, onClose, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [deleteTag, setDeleteTag] = useState(null);

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrackerTagsApi({ limit: 500, status: "all" });
      setTags(Array.isArray(res?.tags) ? res.tags : []);
    } catch (_e) {
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadTags();
  }, [open, loadTags]);

  const openForm = (tag = null) => {
    setEditTag(tag);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    const tag = deleteTag;
    setDeleteTag(null);
    if (!tagId(tag)) return;
    try {
      const res = await deleteTrackerTagApi(tagId(tag));
      if (res?.code === 200) {
        enqueueSnackbar(res.message || "Tag deleted", { variant: "success" });
        setTags((prev) => prev.filter((t) => tagId(t) !== tagId(tag)));
        onChanged?.({ type: "deleted", tag });
      } else {
        enqueueSnackbar(res?.message || "Could not delete tag", {
          variant: "error",
        });
      }
    } catch (_e) {
      enqueueSnackbar("Could not delete tag", { variant: "error" });
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 400 }, maxWidth: "100vw" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: INK }}>
            Manage tags
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <button
              type="button"
              className="small-contained-button"
              onClick={() => openForm(null)}
            >
              New Tag
            </button>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 1, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: PRIMARY }} />
            </Box>
          ) : tags.length === 0 ? (
            <Typography sx={{ py: 3, fontSize: 13.5, color: MUTED }}>
              No tags yet — add one with New Tag.
            </Typography>
          ) : (
            tags.map((tag) => (
              <Box
                key={tagId(tag)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 0.9,
                  borderBottom: "1px solid #f1f5f9",
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: tag.color || PRIMARY,
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: INK,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tag.name}
                  {tag.status && tag.status !== "active" ? (
                    <Box
                      component="span"
                      sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 600, color: MUTED }}
                    >
                      · Inactive
                    </Box>
                  ) : null}
                </Typography>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openForm(tag)}>
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTag(tag)}
                    sx={{ color: "#dc2626" }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      </Drawer>

      <AddOrEditTagDrawer
        open={formOpen}
        tag={editTag}
        onClose={() => {
          setFormOpen(false);
          setEditTag(null);
        }}
        onSaved={(saved) => {
          onChanged?.({ type: "saved", tag: saved, prevName: editTag?.name });
          loadTags();
        }}
      />

      <CustomConfirmation
        openDelete={Boolean(deleteTag)}
        setOpenDelete={(value) => {
          if (!value) setDeleteTag(null);
        }}
        title={`Delete tag "${deleteTag?.name || ""}"?`}
        handleAgree={handleDelete}
      />
    </>
  );
}
